import { useEffect, useState } from 'react'
import { attachmentContentUrl } from '@/utils/attachmentStorage'
import { getIdToken } from '@/services/auth'
import { getActingAs } from '@/state/actingAs'

export interface DocumentBlob {
  /** For an <img>, an <embed> or a download link. Revoked when the id changes. */
  objectUrl: string | null
  /** For the parsers, which want the bytes rather than a URL. */
  blob: Blob | null
  loading: boolean
  error: string | null
}

/**
 * Fetches an attachment through the authenticated API route, which proxies the
 * bytes from S3 so the browser never has to reach the bucket itself.
 */
export function useDocumentBlob(attachmentId: string): DocumentBlob {
  const [state, setState] = useState<DocumentBlob>({
    objectUrl: null,
    blob: null,
    loading: true,
    error: null,
  })

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null

    setState({ objectUrl: null, blob: null, loading: true, error: null })

    void (async () => {
      try {
        const token = await getIdToken()
        if (cancelled) return

        const actingAs = getActingAs()
        const response = await fetch(attachmentContentUrl(attachmentId), {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(actingAs ? { 'X-Act-As-Organization-Id': actingAs.organizationId } : {}),
          },
        })
        if (cancelled) return

        if (!response.ok) {
          setState({ objectUrl: null, blob: null, loading: false, error: 'Could not load the document.' })
          return
        }

        const blob = await response.blob()
        if (cancelled) return

        objectUrl = URL.createObjectURL(blob)
        setState({ objectUrl, blob, loading: false, error: null })
      } catch {
        if (cancelled) return
        setState({ objectUrl: null, blob: null, loading: false, error: 'Could not load the document.' })
      }
    })()

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [attachmentId])

  return state
}
